"use client";

import Image from "next/image";
import Link from "next/link";
import { SOCIAL_LINKS } from "@/lib/constants";
import { getStoreGallery } from "@/lib/store-hero-images";

const STRIP_LENGTH = 12;

type Props = {
  /** The store whose photos fill the strip. Omit for the bundled fallback. */
  covaLocationId?: number | null;
  imageUrl?: string | null;
  photos?: string[] | null;
};

/**
 * The strip used to be twelve empty cards with a blue gradient on them, so a
 * section headed "Follow the Vibe" showed no vibe at all. It now runs the
 * store's own photographs.
 */
export default function LocationFollowTheVibeSection({
  covaLocationId,
  imageUrl,
  photos,
}: Props = {}) {
  const gallery = getStoreGallery(
    { covaLocationId: covaLocationId ?? 0, imageUrl, photos },
    STRIP_LENGTH
  );
  const BASE_CARDS = gallery.map((src, i) => ({
    id: i,
    src,
    rotation: i % 2 === 0 ? 4 : -4,
  }));
  const CARDS = [...BASE_CARDS, ...BASE_CARDS];

  return (
    <section
      className="mt-16 w-full max-w-none overflow-hidden sm:mt-28"
      aria-label="Follow Dank on social media"
    >
      {/* Header row, padded like the surrounding content */}
      <div className="mb-5 grid grid-cols-1 gap-3 lg:grid-cols-12 lg:items-start px-4 sm:px-8">
        <h2 className="text-h3-mobile font-bold leading-tight text-blue-100 sm:text-h3-desktop lg:col-span-7">
          Follow the Vibe
        </h2>
        <p className="text-body-14-desktop font-medium text-blue-100/80 sm:text-body-16-desktop lg:col-span-5">
          Join our community on social for fresh drops, behind-the-scenes content, and daily inspiration
        </p>
      </div>

      {/*
       * Auto-scrolling strip, full width, no side padding.
       *
       * The mask fades the cards out at both ends so the strip reads as
       * continuing past the viewport rather than being chopped off, and the
       * blur panels over the top thicken toward the edges. Both are masks
       * rather than solid gradients, so neither assumes a page background.
       */}
      <div className="relative">
        <div
          className="w-full overflow-hidden [-webkit-mask-image:linear-gradient(to_right,transparent_0%,black_7%,black_93%,transparent_100%)] [mask-image:linear-gradient(to_right,transparent_0%,black_7%,black_93%,transparent_100%)]"
          aria-hidden="true"
          onMouseEnter={(e) => {
            (e.currentTarget.firstElementChild as HTMLElement | null)?.style.setProperty(
              "animation-play-state",
              "paused"
            );
          }}
          onMouseLeave={(e) => {
            (e.currentTarget.firstElementChild as HTMLElement | null)?.style.setProperty(
              "animation-play-state",
              "running"
            );
          }}
        >
          <div
            className="flex items-center py-8 animate-scroll-left [&>*+*]:-ml-4 sm:[&>*+*]:-ml-7"
            style={{ width: "max-content" }}
          >
            {CARDS.map((card, i) => (
              <Link
                key={i}
                href={SOCIAL_LINKS.instagram ?? "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 block rounded-2xl"
                aria-label="View Dank on Instagram"
                style={{ transform: `rotate(${card.rotation}deg)` }}
              >
                <div className="relative w-[176px] h-[176px] sm:w-[328px] sm:h-[328px] overflow-hidden rounded-2xl border-[5px] border-constant-white shadow-xl bg-[#A5AEDE]">
                  <Image
                    src={card.src}
                    alt=""
                    fill
                    className="object-cover"
                    sizes="(max-width: 640px) 176px, 328px"
                    unoptimized
                  />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Blur thickens toward each end, so cards soften as they leave rather
            than sliding under a hard edge. Masked, not a solid gradient, so it
            works on whatever the page background happens to be. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 w-16 backdrop-blur-[3px] [-webkit-mask-image:linear-gradient(to_right,black,transparent)] [mask-image:linear-gradient(to_right,black,transparent)] sm:w-32"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 w-16 backdrop-blur-[3px] [-webkit-mask-image:linear-gradient(to_left,black,transparent)] [mask-image:linear-gradient(to_left,black,transparent)] sm:w-32"
        />
      </div>
    </section>
  );
}
